import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Linkedin, Twitter, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Logo from './Logo';
import { subscribeNewsletter } from '../services/api';
import { toast } from 'sonner';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await subscribeNewsletter(email);
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const regions = [
    { name: 'Europe', countries: ['Greece', 'UK', 'Portugal'] },
    { name: 'Middle East', countries: ['UAE (Dubai)'] },
    { name: 'Americas', countries: ['USA', 'Canada'] },
    { name: 'Asia', countries: ['Singapore'] },
    { name: 'Africa', countries: ['Egypt'] },
  ];

  // SEO Keywords organized by category
  const seoKeywords = {
    'Golden Visa Programs': [
      { term: 'Greece Golden Visa', search: 'Greece' },
      { term: 'Portugal Golden Visa', search: 'Portugal' },
      { term: 'UAE Golden Visa', search: 'UAE' },
      { term: 'Dubai Golden Visa', search: 'UAE' },
      { term: 'European Golden Visa', search: 'Europe' },
    ],
    'Investor Visas': [
      { term: 'USA EB-5 Visa', search: 'USA' },
      { term: 'UK Investor Visa', search: 'UK' },
      { term: 'Canada Start-Up Visa', search: 'Canada' },
      { term: 'Singapore GIP', search: 'Singapore' },
      { term: 'Egypt Residency', search: 'Egypt' },
    ],
    'Investment Destinations': [
      { term: 'Dubai Property Investment', search: 'Dubai' },
      { term: 'London Real Estate', search: 'London' },
      { term: 'Santorini Villas', search: 'Santorini' },
      { term: 'Miami Waterfront', search: 'Miami' },
      { term: 'Toronto Condos', search: 'Toronto' },
    ],
    'For Indian Investors': [
      { term: 'NRI Investment', search: '' },
      { term: 'HNI Property', search: '' },
      { term: 'Residency by Investment', search: '' },
      { term: 'Second Passport', search: '' },
      { term: 'Overseas Property', search: '' },
    ],
    'Property Types': [
      { term: 'Luxury Villas', search: 'Villa' },
      { term: 'Premium Apartments', search: 'Apartment' },
      { term: 'Beachfront Properties', search: '' },
      { term: 'Penthouse Suites', search: 'Penthouse' },
      { term: 'Investment Estates', search: 'Estate' },
    ],
  };

  return (
    <footer className="bg-[#0B0C10] border-t border-[#2A2F3A]" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <span className="label-overline">Stay Informed</span>
            <h3 className="text-3xl sm:text-4xl text-white mt-4 mb-4">
              Exclusive Investment Opportunities
            </h3>
            <p className="text-[#A1A1AA] mb-8">
              Join our curated list of discerning investors. Receive first access to premium 
              properties and visa program updates across our global portfolio.
            </p>
            <form 
              onSubmit={handleSubscribe} 
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              data-testid="newsletter-form"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-[#1A1D24] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                data-testid="newsletter-email-input"
                required
              />
              <Button 
                type="submit" 
                className="btn-luxury"
                disabled={isSubmitting}
                data-testid="newsletter-submit-btn"
              >
                {isSubmitting ? (
                  'Subscribing...'
                ) : (
                  <>
                    Subscribe <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* SEO Keywords Section */}
      <div className="border-b border-[#2A2F3A] bg-[#0D0E12]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h4 className="text-white font-medium text-center mb-8">Popular Searches</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {Object.entries(seoKeywords).map(([category, keywords]) => (
              <div key={category}>
                <h5 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3">{category}</h5>
                <ul className="space-y-2">
                  {keywords.map((keyword) => (
                    <li key={keyword.term}>
                      <Link
                        to={keyword.search ? `/properties?search=${encodeURIComponent(keyword.search)}` : '/properties'}
                        className="text-[#A1A1AA] text-sm hover:text-white transition-colors"
                        data-testid={`seo-${keyword.term.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {keyword.term}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <Logo size="default" />
            </Link>
            <p className="text-[#A1A1AA] text-sm mb-6">
              Your gateway to global residency through premium real estate investments. 
              Serving discerning Indian investors worldwide.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors" data-testid="social-instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors" data-testid="social-linkedin">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="#" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors" data-testid="social-twitter">
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-medium mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/properties" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm" data-testid="footer-link-properties">
                  Properties
                </Link>
              </li>
              <li>
                <Link to="/visas" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm" data-testid="footer-link-visas">
                  Visa Programs
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm" data-testid="footer-link-contact">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm" data-testid="footer-link-careers">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm" data-testid="footer-link-admin">
                  Admin Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="text-white font-medium mb-6">Destinations</h4>
            <ul className="space-y-3">
              {regions.map((region) => (
                <li key={region.name}>
                  <span className="text-[#D4AF37] text-xs uppercase tracking-wider">{region.name}</span>
                  <div className="text-[#A1A1AA] text-sm mt-1">
                    {region.countries.join(' • ')}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-medium mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-[#A1A1AA] text-sm">
                  Suratwala Mark Plazzo,<br />
                  Hinjawadi Ph. 1, Pune
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-[#D4AF37]" />
                <a href="tel:+918999570132" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">
                  +91 89995 70132
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-[#D4AF37]" />
                <a href="mailto:info@mulberryleaf.in" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">
                  info@mulberryleaf.in
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-[#A1A1AA]">
            <p>© {new Date().getFullYear()} Mulberry World. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Disclaimer</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
